import { useTranslation } from 'react-i18next';
import { BaseModal } from '@/components/modal/BaseModal';
import { useEditorStore } from '../../store/editorStore';
import { Volume2, VolumeX } from 'lucide-react';

interface MobileAudioMixerSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileAudioMixerSheet({ isOpen, onClose }: MobileAudioMixerSheetProps) {
  const { t } = useTranslation();
  const tracks = useEditorStore((s) => s.project.tracks);
  const setTrackVolume = useEditorStore((s) => s.setTrackVolume);
  const setTrackPan = useEditorStore((s) => s.setTrackPan);
  const toggleTrackMute = useEditorStore((s) => s.toggleTrackMute);

  const audioTracks = tracks.filter((tr) => tr.type === 'audio' || tr.type === 'video');

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      mobileVariant="bottomsheet"
      ariaLabel={t('editor.audio', 'Audio')}
    >
      <div className="flex flex-col max-h-[80dvh]">
        {/* Drag handle */}
        <div className="flex justify-center py-2 shrink-0">
          <div className="w-8 h-1 rounded-full bg-white/20" />
        </div>

        <div className="px-4 pb-2 shrink-0">
          <h3 className="text-sm font-semibold text-[var(--user-page-text)]">
            {t('editor.mobile.audioMixer', 'Audio Mixer')}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
          {audioTracks.length === 0 ? (
            <p className="text-xs text-[#8E8E93] py-4 text-center">
              {t('editor.mobile.noAudioTracks', 'No audio tracks')}
            </p>
          ) : (
            <div className="space-y-4">
              {audioTracks.map((track) => (
                <div key={track.id} className="rounded-lg bg-[#1A1A1E] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[#E5E5EA]">{track.name}</span>
                    <button
                      onClick={() => toggleTrackMute(track.id)}
                      className={`p-1.5 rounded transition-colors ${
                        track.muted ? 'text-red-400' : 'text-[#8E8E93] hover:text-[#E5E5EA]'
                      }`}
                    >
                      {track.muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                  </div>

                  {/* Volume slider */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] text-[#8E8E93] w-10">{t('editor.volume', 'Volume')}</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={track.volume}
                      onChange={(e) => setTrackVolume(track.id, parseFloat(e.target.value))}
                      className="flex-1 h-1 accent-[var(--user-page-accent)]"
                    />
                    <span className="text-[10px] text-[#8E8E93] w-8 text-end tabular-nums">
                      {Math.round(track.volume * 100)}%
                    </span>
                  </div>

                  {/* Pan slider */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#8E8E93] w-10">{t('editor.pan', 'Pan')}</span>
                    <input
                      type="range"
                      min={-1}
                      max={1}
                      step={0.01}
                      value={track.pan}
                      onChange={(e) => setTrackPan(track.id, parseFloat(e.target.value))}
                      className="flex-1 h-1 accent-[var(--user-page-accent)]"
                    />
                    <span className="text-[10px] text-[#8E8E93] w-8 text-end tabular-nums">
                      {track.pan === 0 ? 'C' : track.pan < 0 ? `L${Math.round(Math.abs(track.pan) * 100)}` : `R${Math.round(track.pan * 100)}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
